// ZuwendungenKarte.tsx — Karte „Zuwendungen, Veranstaltungen & Newsletter" der
// Kontaktanlage. Seit 27.09.2026 bei Privat UND Firma (bei der Firma nach den
// Mitgliedschaften), vorher nur in der Personenmaske.
//
// Hält Zuwendungen und Einladungen als Listen-State und schreibt sie als EIN
// JSON-Feld `zuwendungen` ins Formular (Format von `zuwendungenAusFeld`).
// Dateien zu Einladungen, die vor dem Anlegen gewählt werden, reicht sie über
// `onEinladungsDatei` an die Maske, die sie nach dem Anlegen ablegt.
//
// Newsletter in beide Richtungen (User 28.09.2026):
//   „Hat der Kontakt einen Newsletter?" → `eigenerNewsletter` ("ja"/"nein"),
//     bei Ja `eigenerNewsletterName` + `eigenenNewsletterAbonniert`
//     (utils/eigenerNewsletter.ts);
//   „Abonniert der Kontakt unseren Newsletter?" → `newsletter`, bei Ja die
//     E-Mail-Adressen in `newsletterEmails` als „a; b"-Liste.

import * as React from "react";
import { makeStyles } from "@fluentui/react-components";
import { SectionCard } from "./formPrimitives";
import {
  ZuwendungenAbschnitt,
  type EinladungEintrag,
  type EinladungPrio,
  type WerLaedtEin,
  type ZuwendungEintrag,
} from "./widgets/ZuwendungenAbschnitt";
import { zuwendungenAusFeld, type ZuwendungItem } from "../relationsInline/ZuwendungenListe";
import { REFINED } from "../risikoanalyseStep/tokens";

/** Trennzeichen der Newsletter-Adressliste — wie bei den übrigen Mehrfachfeldern. */
const TRENNER = "; ";

export const newsletterEmailsLesen = (wert: string): string[] =>
  (wert ?? "")
    .split(";")
    .map((e) => e.trim())
    .filter(Boolean);

const useStyles = makeStyles({
  emails: { display: "flex", flexDirection: "column", rowGap: "6px", marginTop: "12px" },
  frage: { fontFamily: REFINED.fontUI, fontSize: "0.82rem", fontWeight: 600, color: REFINED.ink2 },
  check: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    color: REFINED.ink,
    cursor: "pointer",
    "& input": { accentColor: REFINED.aub, width: "16px", height: "16px", margin: 0 },
  },
  leer: { fontFamily: REFINED.fontUI, fontSize: "0.82rem", color: REFINED.ink3 },
});

export function ZuwendungenKarte({
  f,
  set,
  emailOptionen,
  uploadFile,
  onOpenDocument,
  onEinladungsDatei,
  mitEigenemNewsletter = false,
}: {
  f: (k: string) => string;
  set: (k: string, v: string) => void;
  /** Die E-Mail-Adressen, die der Kontakt in der Maske gerade hat. */
  emailOptionen: Array<{ value: string; label: string }>;
  uploadFile?: (file: File) => Promise<{ url: string; name?: string }>;
  onOpenDocument?: (url: string) => void;
  onEinladungsDatei?: (file: File) => void;
  /**
   * „Hat der Kontakt einen Newsletter?" stellen — nur bei der Firma. Eine
   * Privatperson hat keinen eigenen Newsletter (User 28.09.2026).
   */
  mitEigenemNewsletter?: boolean;
}) {
  const s = useStyles();

  const [zuwendungen, setZuwendungen] = React.useState<ZuwendungEintrag[]>(() =>
    zuwendungenAusFeld(f("zuwendungen"))
      .filter((z) => z.art !== "einladung")
      .map((z) => ({
        id: z.id,
        // Server-Id und Art mitführen (Befund 28.09.2026): ohne `dbId` hielt
        // der Abgleich jede Zeile für neu (löschen + neu anlegen), und ein
        // „Geschenk" wurde beim Speichern zur „Zuwendung".
        ...(z.dbId != null ? { dbId: z.dbId } : {}),
        art: z.art,
        richtung: (z.richtung as "von_uns" | "an_uns") || "von_uns",
        was: z.was,
        wann: z.wann,
        anlass: z.anlass,
      })),
  );

  const [einladungen, setEinladungen] = React.useState<EinladungEintrag[]>(() =>
    zuwendungenAusFeld(f("zuwendungen"))
      .filter((z) => z.art === "einladung")
      .map((z) => ({
        id: z.id,
        ...(z.dbId != null ? { dbId: z.dbId } : {}),
        werLaedtEin: (z.richtung === "an_uns" ? "wir_sind_eingeladen" : "wir_laden_ein") as WerLaedtEin,
        veranstaltung: z.was,
        wann: z.wann,
        status: z.status || (z.richtung === "an_uns" ? "Einladung erhalten" : "Einladung verschickt"),
        prio: (z.prio || "") as EinladungPrio,
        fileUrl: z.fileUrl || "",
        fileName: z.fileName || "",
      })),
  );

  const gemeldeteDateien = React.useRef<Set<File>>(new Set());

  const sync = (zwList: ZuwendungEintrag[], einlList: EinladungEintrag[]) => {
    const alle: ZuwendungItem[] = [
      ...zwList.map((z) => ({
        id: z.id,
        ...(z.dbId != null ? { dbId: z.dbId } : {}),
        art: (z.art && z.art !== "einladung" ? z.art : "zuwendung") as ZuwendungItem["art"],
        richtung: z.richtung,
        was: z.was,
        wann: z.wann,
        anlass: z.anlass,
      })),
      ...einlList.map((e) => ({
        id: e.id,
        ...(e.dbId != null ? { dbId: e.dbId } : {}),
        art: "einladung" as const,
        richtung: e.werLaedtEin === "wir_sind_eingeladen" ? ("an_uns" as const) : ("von_uns" as const),
        was: e.veranstaltung,
        wann: e.wann,
        anlass:
          e.werLaedtEin === "wir_sind_eingeladen"
            ? [e.status, e.fileName ? `Datei: ${e.fileName}` : ""].filter(Boolean).join(" · ")
            : [e.status, e.prio ? `Prio ${e.prio}` : ""].filter(Boolean).join(" · "),
        status: e.status,
        prio: e.prio,
        fileUrl: e.fileUrl,
        fileName: e.fileName,
      })),
    ];
    set("zuwendungen", JSON.stringify(alle));
  };

  const newsletterJa = f("newsletter") === "ja";
  const jaNeinWert = (v: string): boolean | null => (v === "ja" ? true : v === "nein" ? false : null);
  const alsFeld = (v: boolean | null | undefined): string => (v === true ? "ja" : v === false ? "nein" : "");
  const gewaehlteEmails = newsletterEmailsLesen(f("newsletterEmails"));
  // Nur Adressen, die es in der Maske (noch) gibt — eine gelöschte fällt raus.
  const gueltig = gewaehlteEmails.filter((e) => emailOptionen.some((o) => o.value === e));
  const emailUmschalten = (email: string, an: boolean) => {
    const next = an ? [...gueltig.filter((e) => e !== email), email] : gueltig.filter((e) => e !== email);
    set("newsletterEmails", next.join(TRENNER));
  };

  return (
    <SectionCard title="Zuwendungen, Veranstaltungen & Newsletter">
      <ZuwendungenAbschnitt
        zuwendungen={zuwendungen}
        onZuwendungenChange={(list) => {
          setZuwendungen(list);
          sync(list, einladungen);
        }}
        einladungen={einladungen}
        onEinladungenChange={(list) => {
          for (const item of list) {
            if (item.fileRef && !gemeldeteDateien.current.has(item.fileRef)) {
              gemeldeteDateien.current.add(item.fileRef);
              onEinladungsDatei?.(item.fileRef);
            }
          }
          setEinladungen(list);
          sync(zuwendungen, list);
        }}
        diesenKontaktZuVeranstaltungen={Boolean(f("einladungKennzeichen"))}
        onDiesenKontaktChange={(val) => {
          if (val) {
            set("einladungKennzeichen", f("einladungPrioritaet") || "immer");
          } else {
            set("einladungKennzeichen", "");
            set("einladungPrioritaet", "");
          }
        }}
        kontaktPrioritaet={
          (f("einladungPrioritaet") as EinladungPrio) ||
          (["A", "B", "C"].includes(f("einladungKennzeichen")) ? (f("einladungKennzeichen") as EinladungPrio) : "")
        }
        onKontaktPrioritaetChange={(prio) => {
          set("einladungPrioritaet", prio);
          if (prio) {
            set("einladungKennzeichen", prio);
          } else if (f("einladungKennzeichen")) {
            set("einladungKennzeichen", "immer");
          }
        }}
        newsletterEinwilligung={newsletterJa}
        onNewsletterEinwilligungChange={(val) => {
          set("newsletter", val ? "ja" : "");
          if (!val) set("newsletterEmails", "");
          // Bei „Ja" mit genau einer Adresse ist die Antwort klar.
          else if (gueltig.length === 0 && emailOptionen.length === 1) set("newsletterEmails", emailOptionen[0].value);
        }}
        eigenerNewsletter={{
          hat: jaNeinWert(f("eigenerNewsletter")),
          name: f("eigenerNewsletterName"),
          abonniert: jaNeinWert(f("eigenenNewsletterAbonniert")),
        }}
        onEigenerNewsletterChange={
          mitEigenemNewsletter
            ? (patch) => {
                if ("hat" in patch) set("eigenerNewsletter", alsFeld(patch.hat));
                if ("name" in patch) set("eigenerNewsletterName", patch.name ?? "");
                if ("abonniert" in patch) set("eigenenNewsletterAbonniert", alsFeld(patch.abonniert));
              }
            : undefined
        }
        newsletterZusatz={
          newsletterJa ? (
            <div className={s.emails}>
              <span className={s.frage}>An welche E-Mail-Adressen?</span>
              {emailOptionen.length === 0 ? (
                <span className={s.leer}>
                  Noch keine E-Mail-Adresse erfasst — bitte oben unter Kommunikation eintragen.
                </span>
              ) : (
                emailOptionen.map((o) => (
                  <label className={s.check} key={o.value}>
                    <input
                      type="checkbox"
                      checked={gueltig.includes(o.value)}
                      onChange={(e) => emailUmschalten(o.value, e.target.checked)}
                    />
                    {o.label}
                  </label>
                ))
              )}
            </div>
          ) : null
        }
        uploadFile={uploadFile}
        onOpenDocument={onOpenDocument}
      />
    </SectionCard>
  );
}
